import { pages } from './pages.js';

const STORAGE_KEY = 'phixelated:nav-open';

function readStoredState() {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    // Private windows and blocked site-data both throw here.
    return false;
  }
}

function writeStoredState(open) {
  try {
    localStorage.setItem(STORAGE_KEY, String(open));
  } catch {
    // Non-fatal - the nav just will not remember across loads.
  }
}

/**
 * Injects the site nav into every page.
 *
 * It starts tucked away so it never obstructs the view (the calculator is
 * full-bleed 3D). A small chevron tab is the only thing visible until you pull
 * it down; clicking again sends it back up. The choice is remembered per
 * browser.
 */
function mountNav() {
  const current = window.location.pathname.replace(/\/+$/, '') || '/';

  const nav = document.createElement('nav');
  nav.className = 'site-nav';
  nav.setAttribute('aria-label', 'Site');

  const panel = document.createElement('div');
  panel.className = 'site-nav__panel';
  panel.id = 'site-nav-panel';

  const entries = [
    { slug: '', title: 'Home' },
    ...pages.filter((p) => p.status === 'live').map((p) => ({ slug: p.slug, title: p.navTitle ?? p.title })),
  ];

  for (const entry of entries) {
    const href = entry.slug ? `/${entry.slug}` : '/';
    const link = document.createElement('a');
    link.className = 'site-nav__link';
    link.href = href;
    link.textContent = entry.title;

    if (href === current) {
      link.classList.add('is-current');
      link.setAttribute('aria-current', 'page');
    }
    panel.append(link);
  }

  const tab = document.createElement('button');
  tab.className = 'site-nav__tab';
  tab.type = 'button';
  tab.setAttribute('aria-controls', 'site-nav-panel');

  const chevron = document.createElement('span');
  chevron.className = 'site-nav__chevron';
  chevron.setAttribute('aria-hidden', 'true');
  chevron.textContent = '\u25BE'; // ▾
  tab.append(chevron);

  nav.append(panel, tab);

  function setOpen(open) {
    nav.dataset.open = String(open);
    tab.setAttribute('aria-expanded', String(open));
    tab.setAttribute('aria-label', open ? 'Hide navigation' : 'Show navigation');
    writeStoredState(open);
  }

  tab.addEventListener('click', () => setOpen(nav.dataset.open !== 'true'));

  // Esc tucks it away again - handy on the calculator.
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && nav.dataset.open === 'true') setOpen(false);
  });

  setOpen(readStoredState());
  document.body.append(nav);
}

export { mountNav };
